import { request } from '../core';
import BaseOptions from './base';
import { CommonResponse } from '@/utils/types';

// 公共附件类型
export interface AttachmentItem {
  type: string; // 文件类型
  fileKey: string; // 文件名 key
  url?: string; // 文件访问地址（历史数据中可能有）
}

/** 维修事项列表入参 */
export interface GetRepairOrdersListParams {
  vehicleName: string; // 车号
  finish: number; // 状态: 进行中0, 已完成1, 待验收2
  onlyVehicle: number; // 是否只看单车: 只看单车1, 查看站点下0
  erp?: string | null; // erp（cookie中获取，这里入参可选）
}

/** 维修事项列表单条结构 */
export interface RepairOrderItem {
  vehicleName: string;
  requireNumber: string;
  type: string | null;
  typeName: string | null;
  reportTime: string; // ISO or readable time string
  reportErp: string;
  status: number; // 状态值
  statusName: string; // 状态名称
  description: string | null;
  influenceOperation: number; // 是否影响运营 1/0
  pictureList?: string[] | null; // 图片列表
  video?: string | null; // 视频
  endTime?: string | null; // 结单时间
  notAcceptReason?: string | null; // 不受理原因
  attachmentList: AttachmentItem[] | null;
}

/** 历史问题反馈入参 */
export interface GetIssueFeedbackHistoryParams {
  stationId?: number; // 显示站点下全部车辆时传入
  vehicleName?: string; // 车牌号，显示全部车辆时不传
  feedbackStatus?: string[]; // 反馈单状态，不传返回全量
  searchRemoteControl?: boolean; // 是否查询帮忙遥控类型，必填，默认 false，返回非帮忙遥控类型单
}

/** 历史问题反馈单条结构 */
export interface IssueFeedbackHistoryItem {
  vehicleName: string;
  supplier: string | null;
  feedbackType: string | null;
  feedbackTypeName: string | null;
  remoteControlType: string | null;
  remoteControlTypeName: string | null;
  feedbackStatus: string | null;
  feedbackStatusName: string | null;
  reporterErp: string | null;
  createTime: string; // 提报时间
  description: string | null;
  affectOperation: boolean | null;
  completeTime?: string | null; // 完成或驳回时间
  rejectReason?: string | null; // 驳回原因
  attachmentList: AttachmentItem[] | null;
}

//TODO: remove erp?
class HistoryIssueOrdersApi extends BaseOptions {
  /**
   * 小哥工作台查询维修事项列表
   * @param {GetRepairOrdersListParams} params
   * @return {*}  {Promise<CommonResponse<RepairOrderItem[]>>}
   * @memberof HistoryIssueOrdersApi
   */
  public getRepairOrdersList(
    params: GetRepairOrdersListParams
  ): Promise<CommonResponse<RepairOrderItem[]>> {
    const requestOption = {
      headers: this.requestHeaders,
      method: 'POST',
      absoluteURL:
        this.fetchDomain + '/k2/management/workbench/require/get_require_list',
      body: params
    };
    return request(requestOption);
  }

  /**
   * 维修单验收不通过（拒绝通过）
   * @param {({
   *     operationSource: string; // 固定 'WORKBENCH_MONITOR'
   *     requireNumber: string;
   *     rejectReason: string;
   *     erp?: string | null;
   *   })} params
   * @return {*}  {Promise<CommonResponse<null>>}
   * @memberof HistoryIssueOrdersApi
   */
  public rejectRequire(params: {
    operationSource: string; // 固定 'WORKBENCH_MONITOR'
    requireNumber: string;
    rejectReason: string;
    erp?: string | null;
  }): Promise<CommonResponse<null>> {
    const requestOption = {
      headers: this.requestHeaders,
      method: 'POST',
      absoluteURL:
        this.fetchDomain + '/k2/management/mobile/require/reject_require',
      body: params
    };
    return request(requestOption);
  }

  /**
   * 维修单验收通过（通过）
   * @param {({
   *     operationSource: string; // 固定 'WORKBENCH_MONITOR'
   *     requireNumber: string;
   *     erp?: string | null;
   *   })} params
   * @return {*}  {Promise<CommonResponse<null>>}
   * @memberof HistoryIssueOrdersApi
   */
  public checkRequire(params: {
    operationSource: string; // 固定 'WORKBENCH_MONITOR'
    requireNumber: string;
    erp?: string | null;
  }): Promise<CommonResponse<null>> {
    const requestOption = {
      headers: this.requestHeaders,
      method: 'POST',
      absoluteURL:
        this.fetchDomain + '/k2/management/mobile/require/check_require',
      body: params
    };
    return request(requestOption);
  }

  /**
   * 将车辆下所有待验收的维修单验收通过
   * @param {{
   *     vehicleName: string;
   *   }} params
   * @return {*}  {Promise<CommonResponse<null>>}
   * @memberof HistoryIssueOrdersApi
   */
  public checkVehicleAllRequire(params: {
    vehicleName: string;
  }): Promise<CommonResponse<null>> {
    const requestOption = {
      headers: this.requestHeaders,
      method: 'POST',
      absoluteURL:
        this.fetchDomain +
        '/k2/management/workbench/require/check_vehicle_all_require',
      body: params
    };
    return request(requestOption);
  }

  /**
   * 历史问题反馈记录列表
   * @param {GetIssueFeedbackHistoryParams} params
   * @return {*}  {Promise<CommonResponse<IssueFeedbackHistoryItem[]>>}
   * @memberof HistoryIssueOrdersApi
   */
  public getIssueFeedbackHistory(
    params: GetIssueFeedbackHistoryParams
  ): Promise<CommonResponse<IssueFeedbackHistoryItem[]>> {
    const requestOption = {
      headers: this.requestHeaders,
      method: 'POST',
      absoluteURL: this.fetchDomain + '/ticket/data/get_issue_feedback_history',
      body: params
    };
    return request(requestOption);
  }

  /**
   * 结束「极端天气无法跑行」或「修路/站点搬迁无法跑行」工单
   * @param {{
   *     vehicleName: string;
   *   }} params
   * @return {*}  {Promise<CommonResponse<null>>}
   * @memberof HistoryIssueOrdersApi
   */
  public endException(params: {
    vehicleName: string;
  }): Promise<CommonResponse<null>> {
    const requestOption = {
      headers: this.requestHeaders,
      method: 'POST',
      absoluteURL: this.fetchDomain + '/schedule/generalTransport/endException',
      body: params
    };
    return request(requestOption);
  }
}

export const historyIssueOrdersApi = new HistoryIssueOrdersApi();
